import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import config from '../config';

function EnquiryDetailsPage() {
  const { id: pathId } = useParams();
  const [searchParams] = useSearchParams();
  const queryId = searchParams.get('id');
  
  // Extract ID from query parameter (?id=42) or path parameter (/enquiry-details/42)
  const id = queryId || pathId;
  const navigate = useNavigate();

  const [enquiry, setEnquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Fetch enquiry details on page load using extracted ID
  useEffect(() => {
    const fetchEnquiryDetails = async () => {
      if (!id) {
        setLoading(false);
        setFetchError('No enquiry ID specified in the URL. Please provide ?id=42 in the link.');
        return;
      }
      setLoading(true);
      setFetchError(null);

      try {
        const response = await fetch(`${config.apiBaseUrl}/contacts/api/${id}`);
        
        if (!response.ok) {
          throw new Error(`Enquiry record #${id} not found.`);
        }
        
        const result = await response.json();
        const data = result.data || result;

        setEnquiry(data);
      } catch (err) {
        console.error('Error fetching enquiry details:', err);
        setFetchError(err.message || 'Failed to load enquiry details');
      } finally {
        setLoading(false);
      }
    };

    fetchEnquiryDetails();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      {/* Header Banner */}
      <section className="page-header">
        <div className="page-header-bg" style={{
          backgroundImage: `url('${config.baseUrl}/assets/images/backgrounds/Contact-PAGE-HEADER-(1894-434).jpg')`,
        }}>
        </div>
        <div className="container">
          <div className="page-header__inner">
            <h2>Admin Enquiry View</h2>
            <ul className="thm-breadcrumb list-unstyled">
              <li><a href="/">Home</a></li>
              <li><span>/</span></li>
              <li><a href="/contact">Enquiry</a></li>
              <li><span>/</span></li>
              <li><span>Record #{id}</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="contact-page py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-9 col-lg-10">
              
              {/* Header Actions */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <button 
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => navigate('/contact')}
                >
                  <i className="fas fa-arrow-left me-2"></i> Back to Contact Page
                </button>
                {enquiry && (
                  <button 
                    className="btn btn-outline-primary btn-sm"
                    onClick={handlePrint}
                  >
                    <i className="fas fa-print me-2"></i> Print Record
                  </button>
                )}
              </div>

              {/* Loading State */}
              {loading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Fetching enquiry details for ID #{id}...</p>
                </div>
              )}

              {/* Error State */}
              {fetchError && !loading && (
                <div className="alert alert-danger text-center my-4 p-4" role="alert">
                  <i className="fas fa-exclamation-circle fa-2x mb-3 text-danger d-block"></i>
                  <h4>Error Loading Record</h4>
                  <p>{fetchError}</p>
                  <div className="mt-3">
                    <button className="thm-btn" onClick={() => navigate('/contact')}>
                      Back to Contact Form
                    </button>
                  </div>
                </div>
              )}

              {/* Read-Only Admin Card View */}
              {!loading && !fetchError && enquiry && (
                <div 
                  className="card shadow-sm border-0" 
                  style={{ borderRadius: '12px', backgroundColor: '#ffffff', overflow: 'hidden' }}
                >
                  {/* Card Title Banner */}
                  <div 
                    className="card-header p-4 text-white d-flex justify-content-between align-items-center"
                    style={{ backgroundColor: 'var(--delogis-base, #5e2ced)' }}
                  >
                    <div>
                      <h3 className="text-white m-0 font-weight-bold">
                        <i className="fas fa-user-circle me-2"></i>
                        {enquiry.name || 'Child Enquiry Record'}
                      </h3>
                      <small className="opacity-75">Submitted Enquiry ID: #{id}</small>
                    </div>
                    {enquiry.locationPreference && (
                      <span className="badge bg-warning text-dark fs-6 px-3 py-2">
                        <i className="fas fa-map-marker-alt me-1"></i>
                        {enquiry.locationPreference || enquiry.location_preference}
                      </span>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="card-body p-4 p-md-5">
                    <div className="row g-4">
                      
                      {/* Name of Child */}
                      <div className="col-md-6">
                        <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                          <label className="text-muted small fw-bold d-block text-uppercase">Child's Name</label>
                          <span className="fs-5 text-dark fw-bold">{enquiry.name || 'N/A'}</span>
                        </div>
                      </div>

                      {/* Age */}
                      <div className="col-md-3 col-6">
                        <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                          <label className="text-muted small fw-bold d-block text-uppercase">Age</label>
                          <span className="fs-5 text-dark fw-bold">{enquiry.age || 'N/A'}</span>
                        </div>
                      </div>

                      {/* Date of Birth */}
                      <div className="col-md-3 col-6">
                        <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                          <label className="text-muted small fw-bold d-block text-uppercase">D.O.B</label>
                          <span className="fs-5 text-dark fw-bold">{enquiry.dob || 'N/A'}</span>
                        </div>
                      </div>

                      {/* Primary Phone */}
                      <div className="col-md-6">
                        <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                          <label className="text-muted small fw-bold d-block text-uppercase">Primary Phone</label>
                          <span className="fs-5 text-dark fw-bold">
                            <i className="fas fa-phone-alt me-2 text-primary"></i>
                            {enquiry.phone1 || enquiry.phone || 'N/A'}
                          </span>
                        </div>
                      </div>

                      {/* Secondary Phone */}
                      <div className="col-md-6">
                        <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                          <label className="text-muted small fw-bold d-block text-uppercase">Secondary Phone</label>
                          <span className="fs-5 text-dark fw-bold">
                            <i className="fas fa-phone-volume me-2 text-secondary"></i>
                            {enquiry.phone2 || 'N/A'}
                          </span>
                        </div>
                      </div>

                      {/* Email Address */}
                      <div className="col-md-6">
                        <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                          <label className="text-muted small fw-bold d-block text-uppercase">Email Address</label>
                          <span className="fs-5 text-dark fw-bold">
                            <i className="fas fa-envelope me-2 text-info"></i>
                            {enquiry.email ? (
                              <a href={`mailto:${enquiry.email}`} className="text-dark text-decoration-none">
                                {enquiry.email}
                              </a>
                            ) : 'N/A'}
                          </span>
                        </div>
                      </div>

                      {/* Address */}
                      <div className="col-12">
                        <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                          <label className="text-muted small fw-bold d-block text-uppercase">Address</label>
                          <span className="fs-6 text-dark">{enquiry.address || 'N/A'}</span>
                        </div>
                      </div>

                      {/* Child's Diagnosis */}
                      <div className="col-md-6">
                        <div className="p-3 rounded" style={{ backgroundColor: '#eef2ff', borderLeft: '4px solid var(--delogis-base, #5e2ced)' }}>
                          <label className="text-primary small fw-bold d-block text-uppercase">Child's Diagnosis</label>
                          <span className="fs-5 text-dark fw-bold">{enquiry.diagnosis || 'Not specified'}</span>
                        </div>
                      </div>

                      {/* Location Preference */}
                      <div className="col-md-6">
                        <div className="p-3 rounded" style={{ backgroundColor: '#fff3cd', borderLeft: '4px solid #ffc107' }}>
                          <label className="text-dark small fw-bold d-block text-uppercase">Preferred Branch / Location</label>
                          <span className="fs-5 text-dark fw-bold">{enquiry.locationPreference || enquiry.location_preference || 'N/A'}</span>
                        </div>
                      </div>

                      {/* Attached Reports & Downloads Section */}
                      <div className="col-12 mt-4">
                        <div className="border rounded p-4" style={{ backgroundColor: '#fafafa' }}>
                          <h5 className="fw-bold mb-3">
                            <i className="fas fa-paperclip me-2 text-danger"></i>
                            Attached Reports & Diagnostic Documents
                          </h5>

                          {(() => {
                            const rawReports = enquiry.reports || enquiry.reports_urls || enquiry.attachments || [];
                            const reportsList = Array.isArray(rawReports) ? rawReports : (rawReports ? [rawReports] : []);

                            if (reportsList.length === 0) {
                              return (
                                <p className="text-muted m-0 fst-italic">
                                  No report files attached with this enquiry.
                                </p>
                              );
                            }

                            return (
                              <div className="row g-3">
                                {reportsList.map((report, index) => {
                                  let fileUrl = '';
                                  let fileName = `Report_${index + 1}`;

                                  if (typeof report === 'string') {
                                    fileUrl = report.startsWith('http') ? report : `${config.apiBaseUrl}/${report.replace(/^\//, '')}`;
                                    fileName = report.split('/').pop();
                                  } else if (report && typeof report === 'object') {
                                    fileUrl = report.url || report.data || '';
                                    fileName = report.name || `Report_${index + 1}`;
                                  }

                                  return (
                                    <div key={index} className="col-md-6">
                                      <div className="d-flex align-items-center justify-content-between p-3 bg-white border rounded shadow-sm">
                                        <div className="d-flex align-items-center text-truncate me-2">
                                          <i className="fas fa-file-pdf fa-2x me-3 text-danger"></i>
                                          <div className="text-truncate">
                                            <strong className="d-block text-dark text-truncate" title={fileName}>
                                              {fileName}
                                            </strong>
                                            <small className="text-muted">Diagnostic Report #{index + 1}</small>
                                          </div>
                                        </div>

                                        <a 
                                          href={fileUrl} 
                                          target="_blank" 
                                          rel="noopener noreferrer" 
                                          download={fileName}
                                          className="btn btn-sm btn-primary text-white text-nowrap"
                                          style={{ borderRadius: '6px' }}
                                        >
                                          <i className="fas fa-download me-1"></i> Download
                                        </a>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default EnquiryDetailsPage;
